import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Share2, Plus, Sparkles } from 'lucide-react';
import '../styles/InstallPrompt.css';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const cardRef = useRef(null);

  // When the modal becomes visible, force scroll to its top.
  // Mobile browsers sometimes restore the prior page's scroll inside the
  // modal overlay or open the card already scrolled to its bottom — this
  // resets both the page scroll and the card's internal scroll.
  useEffect(() => {
    if (showPrompt) {
      requestAnimationFrame(() => {
        if (cardRef.current) cardRef.current.scrollTop = 0;
        // Also reset the overlay's parent scroll just in case
        const overlay = document.querySelector('.install-prompt-overlay');
        if (overlay) overlay.scrollTop = 0;
      });
    }
  }, [showPrompt]);

  useEffect(() => {
    // Check if it's iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed as PWA
    }

    // Check if user dismissed before (reduced to 1 day for better UX)
    const dismissed = localStorage.getItem('cadence-pwa-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 1) {
        return; // Don't show for 1 day after dismiss
      } else {
        // Clear old dismissal so prompt can show again
        localStorage.removeItem('cadence-pwa-dismissed');
      }
    }

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.__cadenceDeferredPrompt = e; // share globally with InstallAppSection
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2000); // Show after 2 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // If another component already captured the prompt (race on mount order)
    if (window.__cadenceDeferredPrompt && !deferredPrompt) {
      setDeferredPrompt(window.__cadenceDeferredPrompt);
      setTimeout(() => setShowPrompt(true), 2000);
    }

    // For iOS, show instructions after delay
    if (isIOSDevice) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPrompt || window.__cadenceDeferredPrompt;
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        console.log('CADENCE PWA: User accepted install');
      }
      window.__cadenceDeferredPrompt = null;
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('cadence-pwa-dismissed', new Date().toISOString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="install-prompt-overlay" data-testid="pwa-install-prompt">
      <div className="install-prompt-card" ref={cardRef}>
        <button className="install-prompt-close" onClick={handleDismiss} aria-label="Close">
          <X size={20} />
        </button>
        
        {/* Gold Foiled C */}
        <div className="install-prompt-logo">
          <img 
            src="/cadence-gold-plate.png" 
            alt="CADENCE" 
            className="install-foil-c"
          />
        </div>
        
        <h3 className="install-prompt-title">
          <Sparkles size={18} className="title-sparkle" />
          Get the CADENCE App
          <Sparkles size={18} className="title-sparkle" />
        </h3>
        
        <p className="install-prompt-tagline">Clinical Advocacy.<br/>Decisive Empowerment.</p>
        
        {isIOS ? (
          <div className="install-prompt-ios">
            <div className="ios-steps">
              <div className="ios-step">
                <div className="ios-step-icon">
                  <Share2 size={20} />
                </div>
                <span>Tap <strong>Share</strong></span>
              </div>
              <div className="ios-step-arrow">→</div>
              <div className="ios-step">
                <div className="ios-step-icon">
                  <Plus size={20} />
                </div>
                <span><strong>Add to Home</strong></span>
              </div>
            </div>
          </div>
        ) : (
          <ul className="install-features">
            <li>Quick access from home screen</li>
            <li>Works offline</li>
            <li>Faster loading</li>
          </ul>
        )}
        
        <div className="install-prompt-actions">
          {!isIOS && (
            <button className="install-btn" onClick={handleInstall} data-testid="install-btn">
              <Download size={20} />
              Install Now
            </button>
          )}
          <button className="install-dismiss-btn" onClick={handleDismiss}>
            {isIOS ? 'Got it' : 'Maybe Later'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
